import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Sprout } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import SprayCard from '../../components/spray/SprayCard';
import sprayService from '../../services/spray';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useAuth } from '../../context/AuthContext';
import type { SprayApplication } from '../../types/spray';
import { cn } from '../../utils/cn';

/** Labels are `filter.<key>` in this page's translations. */
const FILTERS: { key: 'all' | 'created' | 'assigned' | 'in_progress' | 'completed'; status?: string }[] = [
  { key: 'all' },
  { key: 'created', status: 'created' },
  { key: 'assigned', status: 'assigned' },
  { key: 'in_progress', status: 'in_progress' },
  { key: 'completed', status: 'completed' },
];

/**
 * /nursery/spray — nursery spraying. The spray module's own sprays, the ones
 * aimed at nursery rows, positions and places (docs/nursery-spraying-plan.md).
 * Reached from the nursery's Spray card, which shows only for someone holding
 * a spray seat as well as a nursery one. A spray opens on the spray module's
 * page, as any other spray does.
 */
function NurserySprays() {
  const { t } = useTranslation('nursery', { keyPrefix: 'NurserySprays' });
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAccess: canSpray, loading: sprayLoading } = useModuleLicense('spray', ['operator', 'creator', 'admin']);
  const { hasAccess: canCreate } = useModuleLicense('spray', ['creator', 'admin']);
  const [filter, setFilter] = useState('all');
  const [sprays, setSprays] = useState<SprayApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canSpray) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    sprayService.getApplications({ kind: 'nursery', status: FILTERS.find(f => f.key === filter)?.status, limit: 50 })
      .then(res => {
        if (cancelled) return;
        // The list comes back as `applications`, as on the spray dashboard.
        if (res.success) {
          const top = (res as { applications?: SprayApplication[] }).applications;
          setSprays(Array.isArray(top) ? top : Array.isArray(res.data) ? res.data : []);
        }
        else setError(t('loadFailed'));
      })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : t('loadFailed')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [canSpray, filter, t]);

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<Sprout className="h-6 w-6 text-white" />}
        actionButtons={canCreate ? (
          <Button variant="primary" size="sm" onClick={() => navigate('/spray/new?mode=nursery')}>
            <Plus className="h-4 w-4" />
            {t('newSpray')}
          </Button>
        ) : undefined}
      />
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {!sprayLoading && !canSpray ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900">
            {t('seatRequired')}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" role="tablist" aria-label={t('filterByStatus')}>
              {FILTERS.map(f => (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.key}
                  onClick={() => setFilter(f.key)}
                  className={cn(
                    'px-3 py-1.5 rounded-full border text-sm font-medium',
                    filter === f.key
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-white border-neutral-300 text-neutral-700 hover:border-neutral-400',
                  )}
                >
                  {t(`filter.${f.key}`)}
                </button>
              ))}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[0, 1, 2].map(i => <div key={i} className="h-40 bg-neutral-100 rounded-xl animate-pulse" />)}
              </div>
            ) : sprays.length === 0 ? (
              <div className="bg-white border border-neutral-200 rounded-xl p-6 text-center text-sm text-neutral-500">
                {filter === 'all' ? t('noneYet') : t('noneHere')}
                {canCreate && ` ${t('startOne')}`}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {sprays.map(s => <SprayCard key={s.id} spray={s} currentUserId={user?.id} />)}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default NurserySprays;
